import React from "react";
import "style/Jama.scss";

import { Link } from "react-router-dom";

import useJamaLang from "hooks/useJamaLang";
import { shortCode } from "config/i18n/languages";
import { isRtl, jamaCopy, jamaHtmlLang, JamaLang } from "config/i18n/jamaLangs";
import usePageTitle from "hooks/usePageTitle";

import Reveal from "components/Reveal";

import JudoJamaHero from "assets/image/JudoJamaHero.webp";
import JudoJamaSite from "assets/image/JudoJamaSite.webp";

// Index-matched to landing.jama.references.items (judo, archery)
const REFERENCE_SHOTS = [JudoJamaSite];

// Link judojama.com in the visitor's language (German lives at /, the others
// at /<short code>/); the preview hosts (www2.*) link to the preview.
const IS_PREVIEW =
  typeof window !== "undefined" && window.location.hostname.startsWith("www2.");
const siteFor = (lang: JamaLang) => (url: string) =>
  url.replace(
    "https://www.judojama.com/",
    `https://${IS_PREVIEW ? "www2" : "www"}.judojama.com/${
      lang === "de" ? "" : `${shortCode(lang)}/`
    }`,
  );

const Jama = () => {
  const lang = useJamaLang();
  const t = jamaCopy(lang);
  const site = siteFor(lang);
  usePageTitle(t.pageTitle);

  return (
    <div
      className="lp lp--jama"
      lang={jamaHtmlLang(lang)}
      dir={isRtl(lang) ? "rtl" : undefined}
    >
      <header className="lp-hero">
        <div className="lp-hero__media" aria-hidden="true">
          <img src={JudoJamaHero} alt="" />
        </div>
        <div
          className="lp-hero__overlay lp-hero__overlay--jama"
          aria-hidden="true"
        />

        <div className="shell lp-hero__inner">
          <div className="lp-hero__copy">
            <span className="lp-hero__eyebrow">{t.eyebrow}</span>
            <h1 className="lp-hero__title">
              {t.title[0]}
              <br />
              <span className="text-gradient text-gradient--jama">
                {t.title[1]}
              </span>
            </h1>
            <p className="lp-hero__lead">{t.lead}</p>
            <div className="lp-hero__actions">
              <a
                className="btn btn--primary btn--jama"
                href={site(t.url)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t.primary} ↗
              </a>
              <Link className="btn btn--ghost" to="/contact">
                {t.secondary}
              </Link>
            </div>
          </div>

          <div className="lp-hero__visual">
            <div className="lp-frame">
              <img src={JudoJamaSite} alt="judojama.com" />
            </div>
          </div>
        </div>
      </header>

      <main>
        <section className="lp-section lp-section--light">
          <div className="shell">
            <Reveal className="section-head">
              <h2>
                {t.components.title[0]}{" "}
                <span className="accent accent--jama">
                  {t.components.title[1]}
                </span>
              </h2>
            </Reveal>

            <div className="lp-grid">
              {t.components.items.map((item, index) => (
                <Reveal key={item.title} className="lp-cell" delay={index * 60}>
                  <article className="lp-card">
                    <span className="lp-card__index lp-card__index--jama">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="lp-section lp-section--muted">
          <div className="shell">
            <Reveal className="section-head">
              <h2>
                {t.references.title[0]}{" "}
                <span className="accent accent--jama">
                  {t.references.title[1]}
                </span>
              </h2>
            </Reveal>

            <div className="lp-grid lp-grid--single">
              {t.references.items.map((ref, index) => (
                <Reveal key={ref.title} className="lp-cell" delay={index * 90}>
                  <article className="lp-card lp-card--shot">
                    <div className="lp-card__shot">
                      <img
                        src={REFERENCE_SHOTS[index]}
                        alt={ref.link}
                        loading="lazy"
                      />
                    </div>
                    <span className="lp-card__kicker">{ref.sport}</span>
                    <h3>{ref.title}</h3>
                    <p>{ref.text}</p>
                    <div className="lp-card__links">
                      <a
                        className="lp-card__link"
                        href={site(ref.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {ref.link} <span aria-hidden="true">↗</span>
                      </a>
                      {ref.appUrl ? (
                        <a
                          className="lp-card__link"
                          href={ref.appUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {ref.appLink} <span aria-hidden="true">↗</span>
                        </a>
                      ) : null}
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="lp-section lp-section--light">
          <div className="shell">
            <Reveal className="lp-cta lp-cta--jama">
              <div>
                <h2>{t.cta.title}</h2>
                <p>{t.cta.text}</p>
              </div>
              <div className="lp-cta__actions">
                <Link className="btn btn--primary btn--jama" to="/contact">
                  {t.cta.button}
                </Link>
                <a
                  className="btn btn--ghost"
                  href={site(t.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t.cta.secondary} ↗
                </a>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Jama;
